import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Typography,
  Grid,
  Paper
} from '@mui/material'
import { Visibility, VisibilityOff, CheckCircle, Error } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import type { IntegrationConfigCreate, IntegrationConfigUpdate, ServiceType } from '../../types/integration'
import integrationService from '../../services/integrationService'

interface IntegrationConfigFormProps {
  serviceType: ServiceType
  providerName: string
  initialData?: IntegrationConfigUpdate & { config: Record<string, any> }
  integrationId?: string  // Changed from number to string (UUID)
  onSubmit: (data: IntegrationConfigCreate | IntegrationConfigUpdate) => Promise<void>
  onCancel: () => void
}

interface FieldDefinition {
  name: string
  label: string
  type: 'text' | 'password' | 'email' | 'number' | 'boolean'
  required: boolean
  defaultValue?: any
  helperText?: string
  validation?: {
    pattern?: RegExp
    min?: number
    max?: number
    message?: string
  }
}

// Provider-specific field definitions
const PROVIDER_FIELDS: Record<string, FieldDefinition[]> = {
  truebulksms: [
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
      helperText: 'TrueBulkSMS account username'
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      helperText: 'TrueBulkSMS account password'
    },
    {
      name: 'sender',
      label: 'Sender ID (Header)',
      type: 'text',
      required: true,
      defaultValue: 'DHANSK',
      helperText: 'DLT-registered 6-character sender ID (e.g. DHANSK)'
    },
    {
      name: 'peid',
      label: 'PEID (Entity ID)',
      type: 'text',
      required: true,
      helperText: 'DLT Principal Entity ID'
    },
    {
      name: 'default_template_id',
      label: 'Default OTP Template ID',
      type: 'text',
      required: true,
      helperText: 'DLT Content Template ID for OTP SMS'
    },
    {
      name: 'api_url',
      label: 'API URL',
      type: 'text',
      required: false,
      defaultValue: 'http://truebulksms.biz/api.php',
      helperText: 'TrueBulkSMS HTTP API URL'
    }
  ],
  sarvam: [
    {
      name: 'api_key',
      label: 'API Subscription Key',
      type: 'password',
      required: true,
      helperText: 'Sarvam AI API Subscription Key (api-subscription-key)'
    },
    {
      name: 'model',
      label: 'Model Name',
      type: 'text',
      required: false,
      defaultValue: 'sarvam-105b',
      helperText: 'Sarvam AI model (default: sarvam-105b)'
    },
    {
      name: 'api_url',
      label: 'API URL',
      type: 'text',
      required: false,
      defaultValue: 'https://api.sarvam.ai/v1/chat/completions',
      helperText: 'Sarvam AI Chat Completions endpoint URL'
    }
  ],
  razorpay: [
    {
      name: 'key_id',
      label: 'Key ID',
      type: 'text',
      required: true,
      helperText: 'Razorpay API Key ID'
    },
    {
      name: 'key_secret',
      label: 'Key Secret',
      type: 'password',
      required: true,
      helperText: 'Razorpay API Key Secret'
    },
    {
      name: 'webhook_secret',
      label: 'Webhook Secret',
      type: 'password',
      required: true,
      helperText: 'Webhook signature verification secret'
    },
    {
      name: 'test_mode',
      label: 'Test Mode',
      type: 'boolean',
      required: false,
      defaultValue: false,
      helperText: 'Enable test mode for sandbox environment'
    }
  ],
  entotify: [
    {
      name: 'instance_id',
      label: 'Instance ID',
      type: 'text',
      required: true,
      helperText: 'eNotify WhatsApp instance ID (used as the API token in requests)'
    },
    {
      name: 'base_url',
      label: 'Base URL',
      type: 'text',
      required: false,
      defaultValue: 'https://enotify.app/api',
      helperText: 'API base URL (default: https://enotify.app/api)'
    }
  ],
  smtp: [
    {
      name: 'host',
      label: 'SMTP Host',
      type: 'text',
      required: true,
      helperText: 'SMTP server hostname'
    },
    {
      name: 'port',
      label: 'SMTP Port',
      type: 'number',
      required: true,
      defaultValue: 587,
      helperText: 'SMTP server port (587 for TLS, 465 for SSL)',
      validation: { min: 1, max: 65535, message: 'Port must be between 1 and 65535' }
    },
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
      helperText: 'SMTP authentication username'
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      helperText: 'SMTP authentication password'
    },
    {
      name: 'sender_email',
      label: 'Sender Email',
      type: 'email',
      required: true,
      helperText: 'Email address to send from',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email format'
      }
    },
    {
      name: 'use_tls',
      label: 'Use TLS',
      type: 'boolean',
      required: false,
      defaultValue: true,
      helperText: 'Enable TLS encryption'
    }
  ],
  zeptomail: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      required: true,
      helperText: 'ZeptoMail Send Mail Token (Zoho-enczapikey)'
    },
    {
      name: 'sender_email',
      label: 'Sender Email',
      type: 'email',
      required: true,
      helperText: 'Verified sender address in your ZeptoMail domain',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email format'
      }
    }
  ],
  sendgrid: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      required: true,
      helperText: 'SendGrid API key'
    },
    {
      name: 'sender_email',
      label: 'Sender Email',
      type: 'email',
      required: true,
      helperText: 'Verified sender address in your SendGrid account',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email format'
      }
    }
  ],
  delhivery_b2c: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      required: true,
      helperText: 'Delhivery B2C API key'
    },
    {
      name: 'base_url',
      label: 'Base URL',
      type: 'text',
      required: false,
      defaultValue: 'https://track.delhivery.com',
      helperText: 'API base URL (default: https://track.delhivery.com)'
    },
    {
      name: 'timeout',
      label: 'Timeout (seconds)',
      type: 'number',
      required: false,
      defaultValue: 30,
      helperText: 'Request timeout in seconds',
      validation: { min: 5, max: 120, message: 'Timeout must be between 5 and 120 seconds' }
    }
  ],
  shiprocket: [
    {
      name: 'email',
      label: 'API User Email',
      type: 'email',
      required: true,
      helperText: 'Shiprocket API user email (create a separate API user in Settings → API)',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email format'
      }
    },
    {
      name: 'password',
      label: 'API User Password',
      type: 'password',
      required: true,
      helperText: 'Password for the Shiprocket API user'
    },
    {
      name: 'pickup_location',
      label: 'Pickup Location Name',
      type: 'text',
      required: false,
      defaultValue: 'Primary',
      helperText: 'Pickup location name as configured in your Shiprocket account (default: Primary)'
    },
    {
      name: 'base_url',
      label: 'Base URL',
      type: 'text',
      required: false,
      defaultValue: 'https://apiv2.shiprocket.in/v1/external',
      helperText: 'Shiprocket API base URL (leave empty for default)'
    },
    {
      name: 'timeout',
      label: 'Timeout (seconds)',
      type: 'number',
      required: false,
      defaultValue: 30,
      helperText: 'Request timeout in seconds',
      validation: { min: 5, max: 120, message: 'Timeout must be between 5 and 120 seconds' }
    }
  ],
  delhivery_b2b: [
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
      helperText: 'Delhivery B2B username'
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      helperText: 'Delhivery B2B password'
    },
    {
      name: 'base_url',
      label: 'Base URL',
      type: 'text',
      required: false,
      defaultValue: 'https://track.delhivery.com',
      helperText: 'API base URL (default: https://track.delhivery.com)'
    },
    {
      name: 'timeout',
      label: 'Timeout (seconds)',
      type: 'number',
      required: false,
      defaultValue: 30,
      helperText: 'Request timeout in seconds',
      validation: { min: 5, max: 120, message: 'Timeout must be between 5 and 120 seconds' }
    }
  ],
  sarvam_ai: [
    {
      name: 'api_key',
      label: 'API Subscription Key',
      type: 'password',
      required: true,
      helperText: 'Sarvam AI API Subscription Key (api-subscription-key)'
    },
    {
      name: 'model',
      label: 'Model Name',
      type: 'text',
      required: false,
      defaultValue: 'sarvam-105b',
      helperText: 'Sarvam AI model (default: sarvam-105b)'
    },
    {
      name: 'api_url',
      label: 'API URL',
      type: 'text',
      required: false,
      defaultValue: 'https://api.sarvam.ai/v1/chat/completions',
      helperText: 'Sarvam AI Chat Completions endpoint URL'
    }
  ],
  enotify: [
    {
      name: 'instance_id',
      label: 'Instance ID',
      type: 'text',
      required: true,
      helperText: 'eNotify WhatsApp instance ID (used as the API token in requests)'
    },
    {
      name: 'base_url',
      label: 'Base URL',
      type: 'text',
      required: false,
      defaultValue: 'https://enotify.app/api',
      helperText: 'API base URL (default: https://enotify.app/api)'
    },
    {
      name: 'test_phone_number',
      label: 'Test Phone Number',
      type: 'text',
      required: false,
      defaultValue: '918373900645',
      helperText: 'Phone number to test messages (with country code)'
    }
  ]
}

// Mask sensitive credential for display (show first 4 and last 4 characters)
const maskCredential = (value: string): string => {
  if (!value || value.length <= 8) {
    return '••••••••'
  }
  const first4 = value.substring(0, 4)
  const last4 = value.substring(value.length - 4)
  const masked = '•'.repeat(Math.max(value.length - 8, 4))
  return `${first4}${masked}${last4}`
}

const IntegrationConfigForm: React.FC<IntegrationConfigFormProps> = ({
  serviceType,
  providerName,
  initialData,
  integrationId,
  onSubmit,
  onCancel
}) => {
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({})
  const [testingConnection, setTestingConnection] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Get field definitions for this provider
  const fields = PROVIDER_FIELDS[providerName.toLowerCase().replace(/\s+/g, '_')] || []

  // Initialize form with react-hook-form
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    defaultValues: {
      is_active: initialData?.is_active ?? true,
      is_primary: initialData?.is_primary ?? false,
      priority: initialData?.priority ?? 1,
      config: fields.reduce((acc, field) => {
        if (initialData?.config?.[field.name] !== undefined) {
          acc[field.name] = initialData.config[field.name]
        } else if (field.defaultValue !== undefined) {
          acc[field.name] = field.defaultValue
        } else {
          acc[field.name] = field.type === 'boolean' ? false : ''
        }
        return acc
      }, {} as Record<string, any>)
    }
  })

  const togglePasswordVisibility = (fieldName: string) => {
    setShowPasswords(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))
  }

  const handleTestConnection = async () => {
    if (!integrationId) {
      setTestResult({
        success: false,
        message: 'Cannot test connection before saving configuration'
      })
      return
    }

    setTestingConnection(true)
    setTestResult(null)

    try {
      const result = await integrationService.testConnection(integrationId)
      setTestResult({
        success: result.success,
        message: result.message
      })
    } catch (error: any) {
      const detail = error.response?.data?.detail
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Connection test failed'
      setTestResult({ success: false, message })
    } finally {
      setTestingConnection(false)
    }
  }

  const onFormSubmit = async (data: any) => {
    setSubmitting(true)
    setTestResult(null)

    try {
      const payload = {
        is_active: data.is_active,
        is_primary: data.is_primary,
        priority: data.priority,
        config: data.config
      }

      if (!integrationId) {
        // Create mode - include service_type and provider_name
        await onSubmit({
          service_type: serviceType,
          provider_name: providerName,
          ...payload
        } as IntegrationConfigCreate)
      } else {
        // Update mode
        await onSubmit(payload as IntegrationConfigUpdate)
      }
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const renderField = (field: FieldDefinition) => {
    const fieldName = `config.${field.name}`
    const error = errors.config?.[field.name]

    if (field.type === 'boolean') {
      return (
        <Grid item xs={12} key={field.name}>
          <Controller
            name={fieldName as any}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
                label={field.label}
              />
            )}
          />
          {field.helperText && (
            <FormHelperText>{field.helperText}</FormHelperText>
          )}
        </Grid>
      )
    }

    const isPasswordField = field.type === 'password'
    const showPassword = showPasswords[field.name]
    const currentValue = watch(fieldName as any)

    // For edit mode with password fields, show masked value if not being edited
    const displayValue = integrationId && isPasswordField && currentValue && !showPassword
      ? maskCredential(currentValue)
      : currentValue

    return (
      <Grid item xs={12} sm={6} key={field.name}>
        <Controller
          name={fieldName as any}
          control={control}
          rules={{
            required: field.required ? `${field.label} is required` : false,
            pattern: field.validation?.pattern
              ? {
                value: field.validation.pattern,
                message: field.validation.message || 'Invalid format'
              }
              : undefined,
            min: field.validation?.min
              ? {
                value: field.validation.min,
                message: field.validation.message || `Minimum value is ${field.validation.min}`
              }
              : undefined,
            max: field.validation?.max
              ? {
                value: field.validation.max,
                message: field.validation.message || `Maximum value is ${field.validation.max}`
              }
              : undefined
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              fullWidth
              label={field.label}
              type={isPasswordField && !showPassword ? 'password' : field.type === 'number' ? 'number' : 'text'}
              value={isPasswordField && !showPassword ? displayValue : value}
              onChange={(e) => {
                const newValue = field.type === 'number' ? Number(e.target.value) : e.target.value
                onChange(newValue)
              }}
              onBlur={onBlur}
              error={!!error}
              helperText={(error?.message as string) || field.helperText}
              required={field.required}
              InputProps={
                isPasswordField
                  ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility(field.name)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  : undefined
              }
            />
          )}
        />
      </Grid>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Configuration Settings
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Integration Status */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="is_active"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
                  label="Active"
                />
              )}
            />
            <FormHelperText>Enable or disable this integration</FormHelperText>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="is_primary"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
                  label="Set as Primary"
                />
              )}
            />
            <FormHelperText>Use as default provider for this service</FormHelperText>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="priority"
              control={control}
              rules={{
                required: 'Priority is required',
                min: { value: 1, message: 'Priority must be at least 1' }
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  label="Priority"
                  type="number"
                  value={value}
                  onChange={(e) => onChange(Number(e.target.value))}
                  onBlur={onBlur}
                  error={!!errors.priority}
                  helperText={errors.priority?.message || 'Lower number = higher priority'}
                  required
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Provider Credentials
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {fields.map(renderField)}
        </Grid>
      </Paper>

      {/* Test Connection Result */}
      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          icon={testResult.success ? <CheckCircle /> : <Error />}
          sx={{ mb: 3 }}
        >
          {testResult.message}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={submitting || testingConnection}>
          Cancel
        </Button>

        {integrationId && (
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={submitting || testingConnection}
            startIcon={testingConnection ? <CircularProgress size={20} /> : undefined}
          >
            {testingConnection ? 'Testing...' : 'Test Connection'}
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={submitting || testingConnection}
          startIcon={submitting ? <CircularProgress size={20} /> : undefined}
        >
          {submitting ? 'Saving...' : integrationId ? 'Update Configuration' : 'Create Configuration'}
        </Button>
      </Box>
    </Box>
  )
}

export default IntegrationConfigForm
